/*!
 * resource v0.10.0 
 * (c) 2020 Alex Tkachenko
 * Released under the MIT License.
 */
import { pipe, prop, curry } from 'ramda';

function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}

function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}

function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties(Constructor, staticProps);
  return Constructor;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

/* eslint-disable import/prefer-default-export */
var sameResult = function sameResult(x) {
  return x;
};
function isFunction(value) {
  return Object.prototype.toString.call(value) === '[object Function]' || typeof value === 'function';
}

function createResource() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      urlGetter = _ref.urlGetter,
      _ref$middleware = _ref.middleware,
      middleware = _ref$middleware === void 0 ? sameResult : _ref$middleware,
      _ref$config = _ref.config,
      config = _ref$config === void 0 ? {} : _ref$config,
      _ref$headers = _ref.headers,
      headers = _ref$headers === void 0 ? {} : _ref$headers,
      _ref$fetcher = _ref.fetcher,
      fetcher = _ref$fetcher === void 0 ? null : _ref$fetcher;

  return function () {
    var parameters = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var body = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    var caller = fetcher !== null ? fetcher : fetch;
    var prepared = middleware({
      parameters: parameters,
      body: body,
      headers: headers,
      config: config
    });
    return caller(urlGetter(prepared.parameters), _objectSpread2({}, prepared.config, {
      headers: prepared.headers,
      body: prepared.body
    }));
  };
}

var orderCmp = function orderCmp(a, b) {
  return a.order - b.order || a.index - b.index;
};

var transformerTransducer = function transformerTransducer(acc, t) {
  return acc.then.apply(acc, t);
};

var ResourceChain =
/*#__PURE__*/
function () {
  function ResourceChain(parameters) {
    _classCallCheck(this, ResourceChain);

    this.parameters = parameters || {};
  }

  _createClass(ResourceChain, [{
    key: "getResource",
    value: function getResource() {
      if (!this.preparedResource) {
        this.preparedResource = createResource(this.getParameters());
      }

      return this.preparedResource;
    }
  }, {
    key: "getParameters",
    value: function getParameters() {
      if (!this.preparedParameters) {
        this.preparedParameters = _objectSpread2({}, this.parameters, {
          middleware: this.getMiddleware()
        });
      }

      return this.preparedParameters;
    }
  }, {
    key: "getMiddleware",
    value: function getMiddleware() {
      if (this.preparedMiddleware) {
        return this.preparedMiddleware;
      }

      var _this$parameters$midd = this.parameters.middleware,
          middleware = _this$parameters$midd === void 0 ? [] : _this$parameters$midd;
      this.preparedMiddleware = middleware.length ? pipe.apply(this, middleware.sort(orderCmp).map(prop('middleware'))) : sameResult;
      return this.preparedMiddleware;
    }
  }, {
    key: "getTransformers",
    value: function getTransformers() {
      if (this.preparedTransformers) {
        return this.preparedTransformers;
      }

      var _this$parameters$tran = this.parameters.transformers,
          transformers = _this$parameters$tran === void 0 ? [] : _this$parameters$tran;
      this.preparedTransformers = transformers.sort(orderCmp).map(prop('args'));
      return this.preparedTransformers;
    }
    /**
     * Overrides `urlGetter`.
     *
     * @param {Function} urlGetter - Function that returns url based on passed
     *  parameters.
     * @returns Resource - New Resource instance with overrided urlGetter.
     * @memberof Resource
     */

  }, {
    key: "url",
    value: function url(urlGetter) {
      return new this.constructor(_objectSpread2({}, this.parameters, {
        urlGetter: urlGetter
      }));
    }
    /**
     * Overrides `fetcher`.
     *
     * @param {Function} fetcher - Function that implements Fetch API.
     * @returns Resource - New Resource instance with overrided fetcher.
     * @memberof Resource
     */

  }, {
    key: "fetcher",
    value: function fetcher(_fetcher) {
      return new this.constructor(_objectSpread2({}, this.parameters, {
        fetcher: _fetcher
      }));
    }
    /**
     * Adds new middleware to the set of existing middlewares list.
     *
     * @param {Function} middleware - Middleware that transforms parameters,
     *  config, body, headers to fit your needs.
     * @param {int} order - Middleware execution order. Must be between 0 - 1000.
     *  Default is 500.
     * @returns Resource - New Resource instance with added middleware.
     * @memberof Resource
     */

  }, {
    key: "middleware",
    value: function middleware(_middleware) {
      var order = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 500;
      var previous = this.parameters.middleware || [];
      return new this.constructor(_objectSpread2({}, this.parameters, {
        middleware: [{
          middleware: _middleware,
          order: order,
          index: previous.length
        }].concat(previous)
      }));
    }
    /**
     * Adds new transformer to the set of existing transformers list.
     *
     * @param {Function} then - Transformer that transforms result body.
     * @param {Function} catcher - Error catcher.
     * @param {int} order - Transformer execution order. Must be between 0 - 1000.
     *  Default is 500.
     * @returns Resource - New Resource instance with added transformer.
     * @memberof Resource
     */

  }, {
    key: "transform",
    value: function transform(then, catcher) {
      var order = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 500;
      var args = [then];
      var previous = this.parameters.transformers || [];

      if (isFunction(catcher)) {
        args.push(catcher);
      }

      return new this.constructor(_objectSpread2({}, this.parameters, {
        transformers: [{
          args: args,
          order: order,
          index: previous.length
        }].concat(previous)
      }));
    }
  }, {
    key: "merge",
    value: function merge(config, key) {
      var value = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
      var object = value !== null ? _defineProperty({}, key, value) : key;
      return new this.constructor(_objectSpread2({}, this.parameters, _defineProperty({}, config, _objectSpread2({}, this.parameters[config], {}, object))));
    }
    /**
     * Object to merge into `config`(Fetch API configuration object).
     *
     * @param {*} key - Key for a singular change. Or Object with multiple
     *  key-value pairs.
     * @param {*} value - Value for provided key. Or nothing if the first
     *  argument is Object.
     * @returns Resource - New Resource instance with changed Fetch API config.
     * @memberof Resource
     */

  }, {
    key: "config",
    value: function config(key, value) {
      return this.merge('config', key, value);
    }
    /**
     * Object to merge into `headers` configuration.
     *
     * @param {*} key - Key for a singular change. Or Object with multiple
     *  headers.
     * @param {string} value - Value for provided key. Or nothing if the first
     *  argument is Object.
     * @returns Resource - New Resource instance with changed headers.
     * @memberof Resource
     */

  }, {
    key: "headers",
    value: function headers(key, value) {
      return this.merge('headers', key, value);
    }
    /**
     * Make API call to the resource
     *
     * @param {*} args - Arguments, required to make an API call.
     * @returns Promise - Result of the api call.
     * @memberof Resource
     */

  }, {
    key: "makeRequest",
    value: function makeRequest() {
      var resource = this.getResource();
      return resource.apply(this, arguments); // eslint-disable-line prefer-rest-params
    }
  }, {
    key: "applyTransformers",
    value: function applyTransformers(promise) {
      var transformers = this.getTransformers();
      return transformers.reduce(transformerTransducer, promise);
    }
    /**
     * Execute chain
     *
     * @param {*} args - Arguments, required to make an API call.
     * @returns Promise - Transformed result of api call.
     * @memberof Resource
     */

  }, {
    key: "execute",
    value: function execute() {
      return this.applyTransformers(this.makeRequest.apply(this, arguments) // eslint-disable-line prefer-rest-params
      );
    }
  }]);

  return ResourceChain;
}();

var keyMiddleware = curry(function (key, partialMiddleware, params) {
  return _objectSpread2({}, params, _defineProperty({}, key, partialMiddleware((params || {})[key])));
});
var configMiddleware = keyMiddleware('config');
var parametersMiddleware = keyMiddleware('parameters');
var headersMiddleware = keyMiddleware('headers');
var bodyMiddleware = keyMiddleware('body');
var defaultsMiddleware = function defaultsMiddleware(defaults) {
  return function (parameters) {
    return _objectSpread2({}, defaults, {}, parameters);
  };
};
var overrideMiddleware = function overrideMiddleware(override) {
  return function (parameters) {
    return _objectSpread2({}, parameters, {}, override);
  };
};
var jsonRequestMiddleware = pipe(headersMiddleware(overrideMiddleware({
  'Content-Type': 'application/json'
})), bodyMiddleware(function (body) {
  return typeof body === 'undefined' || body === null ? body : JSON.stringify(body);
}));

var baseResource = new ResourceChain().config({
  mode: 'same-origin',
  cache: 'default',
  credentials: 'same-origin',
  redirect: 'follow',
  referrer: 'client'
});
var receiveResource = baseResource.config({
  method: 'GET'
});
var sendResource = baseResource.config({
  method: 'POST'
});

export { ResourceChain, baseResource, bodyMiddleware, configMiddleware, createResource, defaultsMiddleware, headersMiddleware, jsonRequestMiddleware, keyMiddleware, overrideMiddleware, parametersMiddleware, receiveResource, sendResource };
